import { useTranslations } from '@/hooks/useTranslations';
import { formatDate } from '@/lib/utils';
import { LinkedPurchaseOrder } from '@/types';
import { Link } from '@inertiajs/react';
import { ShoppingCart } from 'lucide-react';
import { route } from 'ziggy-js';

const STATUS_COLORS: Record<string, string> = {
    draft: 'border-border bg-muted text-muted-foreground',
    ordered: 'border-transparent bg-info-subtle text-info-subtle-foreground',
    partially_received: 'border-transparent bg-warning-subtle text-warning-subtle-foreground',
    received: 'border-transparent bg-success-subtle text-success-subtle-foreground',
    cancelled: 'border-transparent bg-danger-subtle text-danger-subtle-foreground',
};

function formatCurrency(amount: number | string | null | undefined, currency: string): string {
    return new Intl.NumberFormat('lt-LT', { style: 'currency', currency: currency || 'EUR' }).format(Number(amount ?? 0));
}

interface LinkedPurchaseOrdersSectionProps {
    purchaseOrders: LinkedPurchaseOrder[];
}

export default function LinkedPurchaseOrdersSection({ purchaseOrders }: LinkedPurchaseOrdersSectionProps) {
    const { t } = useTranslations();

    const counted = purchaseOrders.filter((po) => !['draft', 'cancelled'].includes(po.status));
    const total = counted.reduce((sum, po) => sum + Number(po.subtotal ?? 0), 0);
    const currency = purchaseOrders[0]?.currency ?? 'EUR';

    if (purchaseOrders.length === 0) return null;

    return (
        <div className="mt-6">
            <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <ShoppingCart className="text-muted-foreground h-4 w-4" />
                    <h3 className="text-sm font-semibold">{t('purchase_orders.index.title', 'Purchase Orders')}</h3>
                    <span className="text-muted-foreground text-xs">({purchaseOrders.length})</span>
                </div>
                {counted.length > 0 && (
                    <span className="text-sm font-medium">
                        {t('common.costs.total', 'Total')}: {formatCurrency(total, currency)}
                    </span>
                )}
            </div>

            <div className="border-muted overflow-hidden rounded-lg border">
                <table className="w-full border-separate border-spacing-0 text-sm">
                    <thead>
                        <tr className="bg-muted/40 [&>th]:border-b">
                            <th className="text-muted-foreground px-3 py-2 text-left font-medium">
                                {t('purchase_orders.index.columns.po_number', 'PO Number')}
                            </th>
                            <th className="text-muted-foreground px-3 py-2 text-left font-medium">
                                {t('purchase_orders.index.columns.supplier', 'Supplier')}
                            </th>
                            <th className="text-muted-foreground px-3 py-2 text-left font-medium">
                                {t('purchase_orders.index.columns.status', 'Status')}
                            </th>
                            <th className="text-muted-foreground px-3 py-2 text-left font-medium">
                                {t('purchase_orders.index.columns.order_date', 'Order Date')}
                            </th>
                            <th className="text-muted-foreground px-3 py-2 text-right font-medium">
                                {t('purchase_orders.index.columns.total', 'Total')}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {purchaseOrders.map((po) => (
                            <tr
                                key={po.id}
                                className="hover:bg-muted/30 transition-colors [&>td:first-child]:rounded-l-md [&>td:last-child]:rounded-r-md"
                            >
                                <td className="px-3 py-2">
                                    <Link href={route('purchase-orders.show', po.id)} className="text-primary font-medium hover:underline">
                                        {po.po_number}
                                    </Link>
                                </td>
                                <td className="text-muted-foreground px-3 py-2">{po.supplier?.name ?? '—'}</td>
                                <td className="px-3 py-2">
                                    <span
                                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[po.status] ?? ''}`}
                                    >
                                        {t(`purchase_orders.statuses.${po.status}`, po.status.replace(/_/g, ' '))}
                                    </span>
                                </td>
                                <td className="text-muted-foreground px-3 py-2">{formatDate(po.order_date)}</td>
                                <td className="px-3 py-2 text-right font-medium">
                                    {['draft', 'cancelled'].includes(po.status) ? (
                                        <span className="text-muted-foreground line-through">{formatCurrency(po.subtotal, po.currency)}</span>
                                    ) : (
                                        formatCurrency(po.subtotal, po.currency)
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {counted.length < purchaseOrders.length && (
                <p className="text-muted-foreground mt-1.5 text-xs">
                    {t('purchase_orders.costs.draft_cancelled_excluded', 'Draft and cancelled orders are excluded from cost totals.')}
                </p>
            )}
        </div>
    );
}
